import React, { useEffect, useState } from "react";
import { Clock, Truck, Lock } from "lucide-react";

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!target) return null;
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, total: 0 };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, total: diff };
}

export function MarketBanner({ market }) {
  const cd = useCountdown(market?.close_at);
  const isOpen = market?.is_open;
  const lastChance = isOpen && cd && cd.total > 0 && cd.total <= 24 * 3600 * 1000;

  if (!market || !market.active) {
    return (
      <div className="rounded-[16px] bg-[#122117] text-white px-4 py-3 flex items-center gap-3" data-testid="market-banner-closed">
        <Lock size={18} className="text-white/70" />
        <div>
          <div className="font-bold text-sm">Market is closed</div>
          <div className="text-white/70 text-xs">Orders open on the next Market Day.</div>
        </div>
      </div>
    );
  }

  const parts = cd
    ? [
        { v: cd.d, l: "d" },
        { v: cd.h, l: "h" },
        { v: cd.m, l: "m" },
        { v: cd.s, l: "s" },
      ]
    : [];

  return (
    <div className="space-y-2" data-testid="market-banner">
      <div
        className={`rounded-[20px] px-4 py-3.5 text-white shadow-[0_8px_30px_rgba(11,122,59,0.25)] ${
          isOpen ? "bg-gradient-to-br from-[#0B7A3B] to-[#095F2E]" : "bg-[#6B8375]"
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock size={16} />
            <span className="font-bold text-sm">{isOpen ? "Market is OPEN" : "Market closed"}</span>
          </div>
          {market.delivery_date && (
            <div className="flex items-center gap-1.5 text-xs text-white/85">
              <Truck size={14} />
              {new Date(market.delivery_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
            </div>
          )}
        </div>
        {isOpen && cd && (
          <div className="mt-2.5 flex items-center gap-2" data-testid="market-countdown">
            <span className="text-xs text-white/80">Closes in</span>
            <div className="flex gap-1.5">
              {parts.map((p, i) => (
                <div key={i} className="bg-white/15 backdrop-blur rounded-lg px-2 py-1 min-w-[34px] text-center">
                  <span className="font-extrabold text-sm tabular-nums">{String(p.v).padStart(2, "0")}</span>
                  <span className="text-[10px] text-white/70">{p.l}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {lastChance && (
        <div className="rounded-[16px] bg-[#C0392B] text-white px-4 py-2.5 flex items-center gap-2 pm-pop" data-testid="last-chance-banner">
          <span className="text-lg leading-none">&#9203;</span>
          <span className="font-bold text-sm">Last chance! Market closes within 24 hours.</span>
        </div>
      )}
    </div>
  );
}
